import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { CircleNotch, ArrowLeft, ArrowRight, FloppyDisk, CheckCircle } from "@phosphor-icons/react";
import CoPilotPanel, { CoPilotToggle } from "@/components/CoPilotPanel";
import ContextCarryOver from "@/components/ContextCarryOver";
import { GATES, vrioVerdict } from "@/components/blueprints/VrioBlueprint";

const STEPS = GATES.map((g, i) => ({
  n: i + 1, code: String(i + 1), key: g.key, label: g.title, phase: "STRATEGY",
  question: {
    value: "Does this capability exploit market opportunities or neutralise external threats?",
    rarity: "Is this resource or capability controlled by only a few competing firms?",
    inimitability: "Is it highly costly, complex, or legally protected for competitors to duplicate?",
    organization: "Is the firm structurally aligned, managed and funded to exploit this asset?",
  }[g.key],
}));
const LAST = STEPS.length;

export default function Vrio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [data, setData] = useState({ asset: "", value: null, rarity: null, inimitability: null, organization: null });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try { const res = await api.get(`/documents/${id}`); setTitle(res.data.title || ""); setData({ asset: "", value: null, rarity: null, inimitability: null, organization: null, ...(res.data.payload || {}) }); }
      catch { toast.error("Document not found"); navigate("/dashboard"); }
      finally { setLoading(false); }
    })();
  }, [id]); // eslint-disable-line

  const persist = useCallback(async (extra = {}) => {
    setSaving(true);
    try { await api.put(`/documents/${id}`, { title: title || "Untitled VRIO Assessment", payload: data, ...extra }); return true; }
    catch { toast.error("Save failed"); return false; }
    finally { setSaving(false); }
  }, [id, title, data]);

  const cur = STEPS[step - 1];
  const setGate = (v) => setData((d) => ({ ...d, [cur.key]: v }));
  const saveDraft = async () => { if (await persist()) toast.success("Draft saved"); };
  const next = async () => { await persist(); setStep((s) => Math.min(LAST, s + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const back = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const finalise = async () => { if (await persist({ status: "Completed" })) { toast.success("Assessment finalised"); navigate(`/vrio/${id}/view`); } };
  const verdict = vrioVerdict(data);
  const cval = data[cur.key];

  if (loading) return <Layout><div className="flex items-center justify-center py-40 text-slate-400"><CircleNotch size={28} className="animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto w-full py-8 px-6 lg:px-10">
        <button data-testid="back-link" onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft size={16} /> Workspace</button>
        <input data-testid="vrio-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="VRIO assessment title…" className="w-full bg-transparent text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 placeholder:text-slate-300 focus:outline-none border-b border-transparent focus:border-slate-300 pb-1" />

        <div className="mt-4 bg-white border border-slate-200 rounded-sm p-4">
          <label className="block text-xs font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1.5">Capability / Asset under assessment</label>
          <input data-testid="vrio-asset" value={data.asset} onChange={(e) => setData({ ...data, asset: e.target.value })} placeholder="e.g. Proprietary low-temperature catalyst process" className="flex h-10 w-full rounded-sm border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>

        <div className="mt-4 grid grid-cols-4 gap-2" data-testid="vrio-stepper">
          {STEPS.map((s) => (
            <button key={s.n} data-testid={`step-${s.n}`} onClick={() => setStep(s.n)} className={`rounded-sm border px-2 py-2.5 text-center transition-all ${step === s.n ? "border-indigo-500 bg-indigo-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <span className={`font-mono text-xs font-bold block ${step === s.n ? "text-indigo-700" : "text-slate-400"}`}>{s.label[0]}</span>
              <p className={`text-[11px] font-medium ${step === s.n ? "text-slate-900" : "text-slate-500"}`}>{s.label}</p>
            </button>
          ))}
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-sm shadow-sm p-6 space-y-5">
          <h2 className="text-xl font-semibold text-slate-900">{cur.label}</h2>
          <p className="text-base text-slate-700">{cur.question}</p>
          <div className="flex gap-3" data-testid="vrio-gate-buttons">
            <button data-testid="vrio-yes" onClick={() => setGate(true)} className={`flex-1 rounded-sm border px-4 py-3 text-sm font-semibold transition-all ${cval === true ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}>Yes</button>
            <button data-testid="vrio-no" onClick={() => setGate(false)} className={`flex-1 rounded-sm border px-4 py-3 text-sm font-semibold transition-all ${cval === false ? "border-red-500 bg-red-50 text-red-700" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}>No</button>
          </div>
          <div className={`rounded-sm border p-3 text-center text-sm font-semibold ${verdict.cls}`} data-testid="vrio-running-verdict">{verdict.label}</div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-3 sticky bottom-0 bg-slate-50 py-4">
          <button data-testid="vrio-back" onClick={back} disabled={step === 1} className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"><ArrowLeft size={16} /> Back</button>
          <div className="flex items-center gap-3">
            <button data-testid="vrio-save-draft" onClick={saveDraft} disabled={saving} className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">{saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />} Save Draft</button>
            {step < LAST ? (
              <button data-testid="vrio-next" onClick={next} className="inline-flex items-center gap-2 rounded-sm bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700">Next Gate <ArrowRight size={16} /></button>
            ) : (
              <button data-testid="vrio-finalise" onClick={finalise} disabled={saving} className="inline-flex items-center gap-2 rounded-sm bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700"><CheckCircle size={18} weight="fill" /> Finalise Verdict</button>
            )}
          </div>
        </div>
      </div>
      <CoPilotToggle open={open} onClick={() => setOpen(true)} phase={cur.phase} />
      <CoPilotPanel open={open} onClose={() => setOpen(false)} docId={id} toolType="vrio" step={cur} phase={cur.phase} onApplySuggestion={(t) => { navigator.clipboard?.writeText(t); toast.info("Copied to clipboard"); }} />
    </Layout>
  );
}
